import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { covenantPostgresQuery as commonCovPgQuery } from '@finley-technologies/covenant-calculations';
import {
  CreditAgreement,
  FormulaEngine,
  Util,
} from '@finley-technologies/finley-types';

import { DeleteConfirmationModal } from '../../delete/DeleteArrayPropertyButton';
import { EditPostgresQueryModal } from './EditPostgresQuery';
import { LenderPostgresQueryView } from './lenderView/LenderPostgresQueryView';
import { updatePostgresQueryShell } from './postgresQueryConfig/updateShell';
import {
  AddIconButton,
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import {
  AddQueryInputModal,
  PortfolioCovenantQueryInput,
} from 'packages/settings/ui/AddQueryInputModal';
import { PortfolioCovenantEditTab } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsSidebar';

type PostgresQueryType = CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet;

const createPrincipalBalanceDailyAverageQuery = (
  idx: number,
  wipConfig: WipConfig,
  name?: string,
): CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery => {
  const facilityMechanics: CreditAgreement.DomainEntity.FacilityMechanicsWithEntities = getIn(
    wipConfig.formProps.values,
    'facilityMechanics',
  );

  const capitalProviderSlugs = _.map(
    facilityMechanics.parties.capitalProviders,
    (capitalProvider) => capitalProvider.entity.slug,
  );
  return {
    name: name ?? `PLACEHOLDER_${idx}`,
    variables: [],
    type: 'principalBalanceDailyAverage',
    resultType: 'primitive',
    parameterConfig: {
      startDateFormula: {
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
        type: 'formula',
        formula: 'EOMONTH(triggerDate, -1)',
      },
      endDateFormula: {
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
        type: 'formula',
        formula: 'EOMONTH(triggerDate, -1)',
      },
      capitalProviderSlugs: {
        type: 'capitalProviderSlugArray',
        slugs: capitalProviderSlugs,
      },
    },
  };
};

const createPrincipalBalancePointInTimeQuery = (
  idx: number,
  name?: string,
): CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalancePointInTimeQuery => ({
  name: name ?? `PLACEHOLDER_${idx}`,
  variables: [],
  type: 'principalBalancePointInTime',
  resultType: 'primitive',
  parameterConfig: {
    dateFormula: {
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      type: 'formula',
      formula: 'EOMONTH(triggerDate, -1)',
    },
  },
});

const createTAccountsSumQuery = (
  idx: number,
  name?: string,
): CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery => ({
  name: name ?? `PLACEHOLDER_${idx}`,
  variables: [],
  type: 'tAccountsSum',
  resultType: 'primitive',
  parameterConfig: {
    startDateFormula: {
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      type: 'formula',
      formula: 'EOMONTH(triggerDate, -1)',
    },
    endDateFormula: {
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      type: 'formula',
      formula: 'EOMONTH(triggerDate, -1)',
    },
    tAccountDetails: { type: 'tAccountDetailsArray', accountDetails: [] },
  },
});

const createOrgProvidedInputQuery = (
  idx: number,
  wipConfig: WipConfig,
  name?: string,
): CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery => ({
  name: name ?? `PLACEHOLDER_${idx}`,
  variables: [],
  type: 'orgProvidedInput',
  resultType: 'primitive',
  parameterConfig: {
    reportingDateFormula: {
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      type: 'formula',
      formula: 'EOMONTH(triggerDate, -1)',
    },
    reportingPeriod: {
      type: 'reportingPeriod',
      reportingPeriod: { period: 'calendarDay' },
    },
    key: {
      type: 'orgProvidedInputKey',
      key: wipConfig.orgProvidedInputKeys[0] ?? `PLACEHOLDER_${idx}`,
    },
  },
});

export const createPostgresQuery = ({
  idx,
  type,
  name,
  wipConfig,
}: {
  idx: number;
  type: CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet['type'];
  name?: string;
  wipConfig: WipConfig;
}): CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet => {
  switch (type) {
    case 'principalBalanceDailyAverage':
      return createPrincipalBalanceDailyAverageQuery(idx, wipConfig, name);
    case 'principalBalancePointInTime':
      return createPrincipalBalancePointInTimeQuery(idx, name);
    case 'tAccountsSum':
      return createTAccountsSumQuery(idx, name);
    case 'orgProvidedInput':
      return createOrgProvidedInputQuery(idx, wipConfig, name);
    default:
      return finleyAssertUnreachable(type);
  }
};

type PostgresQueryViewProps = {
  postgresQuery: PostgresQueryType;
  wipConfig?: WipConfig;
  basePropertyKey: string;
  fullPropertyKey: string;
  triggerDate: Util.FullDateStr;
  idx: number;
  setSelectedQueryInput?: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInput | null>
  >; // used to navigate to a new query input, for example when a query is duplicated
  setSelectedTab?: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >; // used to navigate to another tab when the currently viewed input has been deleted
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  selectOptions: {
    value: CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet['type'];
    label: string;
  }[];
};
export const PostgresQueryView = ({
  postgresQuery,
  wipConfig,
  basePropertyKey,
  idx,
  fullPropertyKey,
  triggerDate,
  setSelectedQueryInput,
  setSelectedTab,
  presetVariables,
  inputDateType,
  selectOptions,
}: PostgresQueryViewProps) => {
  const toast = useToast();
  const {
    onOpen: onDuplicateQueryOpen,
    isOpen: isDuplicateQueryOpen,
    onClose: onDuplicateQueryClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();

  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const deleteQuery = useCallback(async () => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    // getIn always returns any, needs typecast to filter
    const postgresQueries = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    ) as PostgresQueryType[];

    const newPostgresQueriesAfterDelete = postgresQueries.filter(
      (_query, varIdx) => varIdx !== idx,
    );

    if (setSelectedTab && setSelectedQueryInput) {
      setSelectedTab('properties');
      setSelectedQueryInput(null);
    }

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: newPostgresQueriesAfterDelete,
      setIsSaveLoading: setIsDeleteLoading,
    });
  }, [basePropertyKey, idx, wipConfig, setSelectedTab, setSelectedQueryInput]);

  const duplicateQuery = useCallback(
    async ({ queryKeyInput }: { queryKeyInput: string }): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      const postgresQueries = getIn(
        wipConfig.formProps.values,
        basePropertyKey,
      );

      const copiedQuery = _.cloneDeep(postgresQuery);
      copiedQuery.name = queryKeyInput;

      const postgresQueriesCopy = _.cloneDeep(postgresQueries);
      postgresQueriesCopy.splice(idx + 1, 0, copiedQuery);

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: basePropertyKey,
        updatedProperty: postgresQueriesCopy,
        setIsSaveLoading: setIsDuplicateLoading,
      });

      if (setSelectedQueryInput) {
        setSelectedQueryInput({
          queryInputType: copiedQuery.type,
          queryInputKey: queryKeyInput,
        });
      }
    },
    [basePropertyKey, idx, postgresQuery, wipConfig, setSelectedQueryInput],
  );

  const onSave = useCallback(
    async (query: PostgresQueryType) => {
      if (wipConfig) {
        const validateResult = commonCovPgQuery.validatePostgresQuery({
          postgresQuery: {
            ...query,
            variables: [
              { name: 'triggerDate', value: triggerDate, parseNumber: true },
              ...query.variables,
            ],
          },
        });
        if (validateResult.errors.length) {
          toast({
            title: JSON.stringify(validateResult.errors),
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }

        await wipConfig.saveAndUpdateConfigCreditAgreementWip({
          wipConfig,
          propertyKey: fullPropertyKey,
          updatedProperty: query,
          setIsSaveLoading,
        });
      }
      setIsOpen(false);
    },
    [wipConfig, fullPropertyKey, toast, triggerDate],
  );

  const isClientWipMode = wipConfig?.operationMode === 'client';

  if (isClientWipMode) {
    return (
      <LenderPostgresQueryView
        postgresQuery={postgresQuery}
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        duplicateParams={{
          isDuplicateQueryOpen,
          onDuplicateQueryOpen,
          onDuplicateQueryClose,
          duplicateQuery,
          isDuplicateLoading,
        }}
        deleteParams={{
          isDeleteConfirmationModalOpen,
          onDeleteConfirmationModalOpen,
          onDeleteConfirmationModalClose,
          deleteQuery,
          isDeleteLoading,
        }}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        presetVariables={presetVariables}
        inputDateType={inputDateType}
        triggerDate={triggerDate}
      />
    );
  }

  return (
    <Tr>
      <Td>{postgresQuery.name}</Td>
      <Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
          <Select
            value={postgresQuery.type}
            onChange={(val) => {
              if (val) {
                const query = _.cloneDeep(
                  getIn(wipConfig.formProps.values, fullPropertyKey),
                ) as PostgresQueryType;
                query.type = val;
                const updatedQuery = updatePostgresQueryShell({
                  query,
                  wipConfig,
                });
                wipConfig.formProps.setFieldValue(
                  fullPropertyKey,
                  updatedQuery,
                );
              }
            }}
            options={selectOptions}
            width="xl"
          />
        ) : (
          <Text>{postgresQuery.type}</Text>
        )}
      </Td>

      <Td>
        <Editor
          defaultLanguage="json"
          value={JSON.stringify(postgresQuery.parameterConfig, null, 4)}
          options={{ readOnly: true, minimap: { enabled: false } }}
          theme="vs-dark"
          height="15vh"
        />
      </Td>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <Td>
          <EditPostgresQueryModal
            isOpen={isOpen}
            onCancel={() => setIsOpen(false)}
            onSave={onSave}
            postgresQuery={postgresQuery}
            wipConfig={wipConfig}
          />
          <CopyIconButton
            ariaLabel="duplicate query"
            label="Duplicate Query"
            onClick={() => onDuplicateQueryOpen()}
          />
          <EditIconButton
            ariaLabel="edit query"
            label="Edit Query"
            onClick={() => setIsOpen(true)}
          />
          <DeleteIconButton
            ariaLabel="delete query"
            label="Delete Query"
            onClick={() => onDeleteConfirmationModalOpen()}
          />
          <AddQueryInputModal
            wipConfig={wipConfig}
            isOpen={isDuplicateQueryOpen}
            onClose={onDuplicateQueryClose}
            onClickAdd={duplicateQuery}
            initialQueryType={postgresQuery.type}
            isAddLoading={isDuplicateLoading}
            mode="duplicate"
          />
          <DeleteConfirmationModal
            isOpen={isDeleteConfirmationModalOpen}
            onClose={onDeleteConfirmationModalClose}
            onClickDelete={deleteQuery}
            isLoading={isDeleteLoading}
          />
        </Td>
      )}
    </Tr>
  );
};

type PostgresQueriesViewProps = {
  postgresQueries: PostgresQueryType[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  triggerDate: Util.FullDateStr;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  selectOptions: {
    value: CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet['type'];
    label: string;
  }[];
};
export const PostgresQueriesView = ({
  wipConfig,
  basePropertyKey,
  postgresQueries,
  triggerDate,
  presetVariables,
  inputDateType,
  selectOptions,
}: PostgresQueriesViewProps) => {
  const addQuery = (wc: WipConfig) => {
    const queries = _.cloneDeep(getIn(wc.formProps.values, basePropertyKey));

    const query = createPostgresQuery({
      idx: queries.length + 1,
      type: 'orgProvidedInput',
      wipConfig: wc,
    });

    queries.push(query);

    wc.formProps.setFieldValue(basePropertyKey, queries);
  };

  return (
    <Flex flexDirection="column" mb="5">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Postgres Queries
        </Text>

        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add query"
            label="Add Query"
            onClick={() => {
              addQuery(wipConfig);
            }}
          />
        )}
      </Flex>

      {postgresQueries.length === 0 && (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no postgres queries.
        </Text>
      )}
      {postgresQueries.length > 0 && (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th> Params</Th>
              {wipConfig && <Th>Edit/Delete</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {_.map(postgresQueries, (postgresQuery, idx) => {
              return (
                <PostgresQueryView
                  key={`${basePropertyKey}-${idx}`}
                  postgresQuery={postgresQuery}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={`${basePropertyKey}[${idx}]`}
                  triggerDate={triggerDate}
                  idx={idx}
                  wipConfig={wipConfig}
                  inputDateType={inputDateType}
                  presetVariables={presetVariables}
                  selectOptions={selectOptions}
                />
              );
            })}
          </Tbody>
        </Table>
      )}
    </Flex>
  );
};
